/**
 * Custom GPT API Client
 * Functions to interact with Custom GPT backend API
 */

import { API_BASE_URL } from '../config'
import type { 
  CustomGPTListResponse, 
  CustomGPTResponse,
  CreateCustomGPTRequest,
  UpdateCustomGPTRequest 
} from '../../types/custom-gpt'

// Helper to normalize API URL (remove trailing slash to avoid double slashes)
const getApiUrl = (path: string): string => {
  const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return `${baseUrl}${normalizedPath}`
}

/** FastAPI returns `{ error?: string }` or `{ detail: string | object[] }` */
function messageFromApiErrorPayload(payload: {
  error?: string
  detail?: string | Array<{ msg?: string }>
}): string {
  if (payload.error) return payload.error
  const d = payload.detail
  if (typeof d === 'string') return d
  if (Array.isArray(d) && d.length > 0) {
    return d.map((e) => e.msg || JSON.stringify(e)).join('; ')
  }
  return 'Request failed'
}

/**
 * Get all Custom GPTs (paginated)
 */
export async function getCustomGPTs(
  page: number = 1,
  limit: number = 20
): Promise<CustomGPTListResponse> {
  const response = await fetch(
    `${getApiUrl('/api/custom-gpts')}?page=${page}&limit=${limit}`,
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch Custom GPTs' }))
    throw new Error(error.error || 'Failed to fetch Custom GPTs')
  }

  return response.json()
}

/**
 * Get a single Custom GPT by ID
 */
export async function getCustomGPT(gptId: string): Promise<CustomGPTResponse> {
  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}`),
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch Custom GPT' }))
    throw new Error(error.error || 'Failed to fetch Custom GPT')
  }

  return response.json()
}

/**
 * Create a new Custom GPT (JSON body — reliable vs large instructions vs multipart limits)
 */
export async function createCustomGPT(data: CreateCustomGPTRequest): Promise<CustomGPTResponse> {
  // Always set required keys so JSON.stringify never drops undefined (which caused 422).
  const payload: Record<string, unknown> = {
    name: data.name ?? '',
    instruction: data.instruction ?? '',
    is_public: Boolean(data.is_public),
  }
  if (data.description != null && String(data.description).trim() !== '') {
    payload.description = data.description
  }
  if (data.conversation_starters && data.conversation_starters.length > 0) {
    payload.conversation_starters = data.conversation_starters.map((s) => String(s))
  }

  const response = await fetch(getApiUrl('/api/custom-gpts'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({}))
    throw new Error(
      messageFromApiErrorPayload(error as { error?: string; detail?: string | Array<{ msg?: string }> }) ||
        'Failed to create Custom GPT'
    )
  }

  return response.json()
}

/**
 * Update an existing Custom GPT
 */
export async function updateCustomGPT(
  gptId: string,
  data: UpdateCustomGPTRequest
): Promise<CustomGPTResponse> {
  const formData = new FormData()
  if (data.name !== undefined) {
    formData.append('name', data.name)
  }
  if (data.description !== undefined) {
    formData.append('description', data.description || '')
  }
  if (data.instruction !== undefined) {
    formData.append('instruction', data.instruction)
  }
  if (data.is_public !== undefined) {
    formData.append('is_public', data.is_public ? 'true' : 'false')
  }
  if (data.conversation_starters !== undefined) {
    formData.append('conversation_starters', JSON.stringify(data.conversation_starters || []))
  }

  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}`),
    {
      method: 'PUT',
      credentials: 'include',
      body: formData,
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to update Custom GPT' }))
    throw new Error(error.error || 'Failed to update Custom GPT')
  }

  return response.json()
}

/**
 * Soft delete a Custom GPT
 */
export async function deleteCustomGPT(gptId: string): Promise<void> {
  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete Custom GPT' }))
    throw new Error(error.error || 'Failed to delete Custom GPT')
  }
}

/**
 * Upload a knowledge base file for a Custom GPT
 */
export async function uploadKnowledgeBaseFile(
  gptId: string,
  file: File
): Promise<{ id: string; file_name: string; file_size: string; file_type: string }> {
  const formData = new FormData()
  formData.append('file', file)

  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}/files`),
    {
      method: 'POST',
      credentials: 'include',
      body: formData,
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to upload file' }))
    throw new Error(error.error || 'Failed to upload file')
  }

  return response.json()
}

/**
 * Delete a knowledge base file
 */
export async function deleteKnowledgeBaseFile(gptId: string, fileId: string): Promise<void> {
  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}/files/${fileId}`),
    {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to delete file' }))
    throw new Error(error.error || 'Failed to delete file')
  }
}

/**
 * Get all knowledge base files for a Custom GPT
 */
export async function getKnowledgeBaseFiles(gptId: string) {
  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}/files`),
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch files' }))
    throw new Error(error.error || 'Failed to fetch files')
  }

  return response.json()
}

/**
 * Create a new chat session for a Custom GPT
 */
export async function createCustomGPTChat(gptId: string): Promise<{ id: string; name: string; created_at: string }> {
  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}/chat/create`),
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to create Custom GPT chat' }))
    throw new Error(error.error || 'Failed to create Custom GPT chat')
  }

  return response.json()
}

/**
 * List all chat sessions for a Custom GPT
 */
export async function listCustomGPTChats(gptId: string): Promise<{ chats: Array<{ id: string; name: string; created_at: string; message_count: number; last_message_at: string | null }> }> {
  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}/chats`),
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch Custom GPT chats' }))
    throw new Error(error.error || 'Failed to fetch Custom GPT chats')
  }

  return response.json()
}

/**
 * Get a specific Custom GPT chat session with messages
 */
export async function getCustomGPTChat(gptId: string, chatId: string): Promise<{
  id: string
  name: string
  created_at: string
  model_name?: string | null
  messages: Array<{
    id: string
    role: string
    content: string
    timestamp: string
    file_path?: string
    file_name?: string
    file_size?: string
  }>
}> {
  const response = await fetch(
    getApiUrl(`/api/custom-gpts/${gptId}/chats/${chatId}`),
    {
      method: 'GET',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  )

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to fetch Custom GPT chat' }))
    throw new Error(error.error || 'Failed to fetch Custom GPT chat')
  }

  return response.json()
}

/**
 * Stream chat with a Custom GPT
 */
export async function customGPTChatStream(
  gptId: string,
  message: string,
  history: { role: 'user' | 'assistant'; content: string }[],
  chatId: string | null,
  file?: File,
  chatModel?: string,
): Promise<Response> {
  const formData = new FormData()
  formData.append('message', message)
  formData.append('history', JSON.stringify(history))
  if (chatId) {
    formData.append('chat_id', chatId)
  }
  if (chatModel) {
    formData.append('chat_model', chatModel)
  }
  if (file) {
    formData.append('file', file)
  }

  const url = getApiUrl(`/api/custom-gpts/${gptId}/chat/stream`)
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to send message to Custom GPT' }))
    throw new Error(error.error || 'Failed to send message to Custom GPT')
  }

  return response
}

export async function editCustomGPTMessage(
  gptId: string,
  chatId: string,
  messageId: string,
  newContent: string,
  history: { role: 'user' | 'assistant'; content: string }[],
  chatModel?: string,
): Promise<Response> {
  const formData = new FormData()
  formData.append('chat_id', chatId)
  formData.append('message_id', messageId)
  formData.append('new_content', newContent)
  formData.append('history', JSON.stringify(history))
  if (chatModel) {
    formData.append('chat_model', chatModel)
  }

  const url = getApiUrl(`/api/custom-gpts/${gptId}/chat/edit-message`)
  const response = await fetch(url, {
    method: 'POST',
    body: formData,
    credentials: 'include',
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to edit message' }))
    throw new Error(error.error || 'Failed to edit message')
  }

  return response
}

